import axios from 'axios';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { api } from '../api/client';
import FullScreenLoader from '../components/FullScreenLoader';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(
    typeof window !== 'undefined' ? localStorage.getItem('org_id') : null
  );

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) loadUser(); else setLoading(false);
  }, []);

  const loadUser = async () => {
    try {
      const response = await api.get('/auth/profile');
      setUser(response.data);
      // Загружаем организации пользователя
      const orgRes = await api.get('/organizations');
      const orgs = orgRes.data;
      setOrganizations(orgs);
      // Выбираем текущую организацию
      let orgId = localStorage.getItem('org_id');
      if (!orgId || !orgs.find(o => o.id === orgId)) {
        orgId = orgs.length > 0 ? orgs[0].id : null;
        localStorage.setItem('org_id', orgId);
      }
      setCurrentOrg(orgId);
      // api добавит X-Organization-Id из localStorage через интерсептор
      return response.data;
    } catch (error) {
      console.error('Error loading user:', error);
      // не стираем токены сразу — пусть перехватчик попробует refresh
      return null;
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { access_token, refresh_token } = response.data;

      localStorage.setItem('access_token', access_token); // <-- access_token
      localStorage.setItem('refresh_token', refresh_token);

      const user = await loadUser();
      return { success: true, token: access_token, user };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.response?.data || error.message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      const loginResult = await login(userData.email, userData.password);
      return loginResult;
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: error.response?.data || error.message };
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('org_id');
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['X-Organization-Id'];
    setUser(null);
    setOrganizations([]);
    setCurrentOrg(null);
  };

  // Функция для смены текущей организации
  const changeOrg = (orgId) => {
    setCurrentOrg(orgId);
    localStorage.setItem('org_id', orgId);
    axios.defaults.headers.common['X-Organization-Id'] = orgId;
  };

  // Функция для перезагрузки пользователя (например, после OAuth)
  const refreshAuth = async () => {
    const token = localStorage.getItem('access_token');
    if (token) {
      return await loadUser();
    }
    return null;
  };

  return (
    <AuthContext.Provider value={{ user, loading, organizations, currentOrg, changeOrg, login, register, logout, refreshAuth }}>
      {loading ? <FullScreenLoader text="Загружаем ваш рабочий стол..." /> : children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
